import { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { PageHeader } from '../components/layout/PageHeader';
import { PageLoader } from '../components/shared/LoadingSpinner';
import { RiskBadge, getRiskLevel } from '../components/shared/RiskBadge';
import { StatusBadge } from '../components/shared/StatusBadge';
import { EmptyState } from '../components/shared/EmptyState';
import { risksAPI } from '../lib/api';
import api from '../lib/api';
import { formatDate, truncate } from '../lib/utils';
import { AlertTriangle, Plus, Trash2, Edit2, Search, X, Sparkles, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Slider } from '../components/ui/slider';
import { toast } from 'sonner';

const RISK_CATEGORIES = [
  'Operational',
  'Technical',
  'Compliance',
  'Financial',
  'Strategic',
  'Security',
  'Privacy',
  'Vendor',
];

const RISK_STATUSES = ['open', 'mitigated', 'accepted'];

export default function RisksPage() {
  const [risks, setRisks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingRisk, setEditingRisk] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [aiAssessing, setAiAssessing] = useState(false);
  const [aiAssessment, setAiAssessment] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'Operational',
    impact: 3,
    likelihood: 3,
    owner: '',
    treatment_plan: '',
    status: 'open',
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchRisks();
  }, []);

  const fetchRisks = async () => {
    try {
      const response = await risksAPI.getAll();
      setRisks(response.data);
    } catch (error) {
      toast.error('Failed to load risks');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (risk = null) => {
    if (risk) {
      setEditingRisk(risk);
      setFormData({
        title: risk.title,
        description: risk.description,
        category: risk.category,
        impact: risk.impact,
        likelihood: risk.likelihood,
        owner: risk.owner || '',
        treatment_plan: risk.treatment_plan || '',
        status: risk.status,
      });
    } else {
      setEditingRisk(null);
      setFormData({
        title: '',
        description: '',
        category: 'Operational',
        impact: 3,
        likelihood: 3,
        owner: '',
        treatment_plan: '',
        status: 'open',
      });
    }
    setIsDialogOpen(true);
  };

  const handleAIAssess = async () => {
    if (!formData.title && !formData.description) {
      toast.error('Please enter a title and description first');
      return;
    }
    
    setAiAssessing(true);
    setAiAssessment(null);
    
    try {
      const response = await api.post('/risks/ai-assess', {
        title: formData.title,
        description: formData.description,
        category: formData.category
      });
      
      setAiAssessment(response.data);
      toast.success('AI assessment complete');
    } catch (error) {
      toast.error('AI assessment failed');
    } finally {
      setAiAssessing(false);
    }
  };

  const applyAIAssessment = () => {
    if (!aiAssessment) return;
    
    setFormData(prev => ({
      ...prev,
      impact: aiAssessment.suggested_impact,
      likelihood: aiAssessment.suggested_likelihood,
      treatment_plan: aiAssessment.mitigation_recommendations?.join('\n') || prev.treatment_plan
    }));
    
    toast.success('AI suggestions applied');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      if (editingRisk) {
        await risksAPI.update(editingRisk.id, formData);
        toast.success('Risk updated successfully');
      } else {
        await risksAPI.create(formData);
        toast.success('Risk added successfully');
      }
      setIsDialogOpen(false);
      fetchRisks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save risk');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this risk?')) return;
    
    try {
      await risksAPI.delete(id);
      toast.success('Risk deleted');
      fetchRisks();
    } catch (error) {
      toast.error('Failed to delete risk');
    }
  };

  const filteredRisks = risks.filter(risk => 
    risk.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    risk.category.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Sort by risk score (highest first)
  const sortedRisks = [...filteredRisks].sort((a, b) => b.risk_score - a.risk_score);

  if (loading) {
    return (
      <DashboardLayout>
        <PageLoader />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <PageHeader 
        title="Risk Register" 
        description="Identify, assess, and track organizational risks"
        actions={
          <Button
            data-testid="add-risk-btn"
            onClick={() => handleOpenDialog()}
            className="bg-slate-900 hover:bg-slate-800 rounded-sm"
          >
            <Plus size={18} className="mr-2" />
            Log Risk
          </Button>
        }
      />

      <div className="max-w-7xl mx-auto px-6 py-8 md:px-8 lg:px-12">
        {/* Search */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              data-testid="risk-search"
              placeholder="Search risks..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-10 rounded-sm"
            />
          </div>
        </div>

        {/* Risk Matrix Summary */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-red-50 border border-red-200 rounded-sm p-4">
            <p className="text-sm font-mono text-red-600 uppercase tracking-wider">High Risk</p>
            <p className="text-2xl font-bold text-red-700 mt-1">
              {risks.filter(r => getRiskLevel(r.risk_score) === 'high').length}
            </p>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-sm p-4">
            <p className="text-sm font-mono text-amber-600 uppercase tracking-wider">Medium Risk</p>
            <p className="text-2xl font-bold text-amber-700 mt-1">
              {risks.filter(r => getRiskLevel(r.risk_score) === 'medium').length}
            </p>
          </div>
          <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-4">
            <p className="text-sm font-mono text-emerald-600 uppercase tracking-wider">Low Risk</p>
            <p className="text-2xl font-bold text-emerald-700 mt-1">
              {risks.filter(r => getRiskLevel(r.risk_score) === 'low').length}
            </p>
          </div>
        </div>

        {/* Risks Table */}
        {sortedRisks.length > 0 ? (
          <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Risk</th>
                    <th>Category</th>
                    <th>Impact</th>
                    <th>Likelihood</th>
                    <th>Score</th>
                    <th>Status</th>
                    <th>Owner</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedRisks.map((risk) => (
                    <tr key={risk.id} className="table-row-hover">
                      <td>
                        <div>
                          <p className="font-medium text-slate-900">{risk.title}</p>
                          <p className="text-xs text-slate-500 mt-0.5">
                            {truncate(risk.description, 50)}
                          </p>
                        </div>
                      </td>
                      <td>
                        <span className="badge badge-neutral">{risk.category}</span>
                      </td>
                      <td className="font-mono text-center">{risk.impact}</td>
                      <td className="font-mono text-center">{risk.likelihood}</td>
                      <td>
                        <RiskBadge score={risk.risk_score} />
                      </td>
                      <td>
                        <StatusBadge status={risk.status} />
                      </td>
                      <td className="text-slate-600">{risk.owner || '-'}</td>
                      <td>
                        <div className="flex items-center gap-2">
                          <button
                            data-testid={`edit-risk-${risk.id}`}
                            onClick={() => handleOpenDialog(risk)}
                            className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                          >
                            <Edit2 size={16} />
                          </button>
                          <button
                            data-testid={`delete-risk-${risk.id}`}
                            onClick={() => handleDelete(risk.id)}
                            className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <EmptyState
            icon={AlertTriangle}
            title="No risks logged"
            description="Start tracking organizational risks to improve your security posture."
            action={
              <Button
                data-testid="empty-add-risk-btn"
                onClick={() => handleOpenDialog()}
                className="bg-slate-900 hover:bg-slate-800 rounded-sm"
              >
                <Plus size={18} className="mr-2" />
                Log Your First Risk
              </Button>
            }
          />
        )}
      </div>

      {/* Add/Edit Risk Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-secondary">
              {editingRisk ? 'Edit Risk' : 'Log New Risk'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div>
              <Label>Title *</Label>
              <Input
                data-testid="risk-title-input"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="e.g., Unauthorized Data Access"
                required
                className="mt-1.5 rounded-sm"
              />
            </div>

            <div>
              <Label>Description *</Label>
              <Textarea
                data-testid="risk-description-input"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Describe the risk and its potential impact..."
                rows={3}
                required
                className="mt-1.5 rounded-sm"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Category</Label>
                <Select 
                  value={formData.category} 
                  onValueChange={(value) => setFormData({ ...formData, category: value })}
                >
                  <SelectTrigger data-testid="risk-category-select" className="mt-1.5 rounded-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {RISK_CATEGORIES.map((cat) => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Status</Label>
                <Select 
                  value={formData.status} 
                  onValueChange={(value) => setFormData({ ...formData, status: value })}
                >
                  <SelectTrigger data-testid="risk-status-select" className="mt-1.5 rounded-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {RISK_STATUSES.map((status) => (
                      <SelectItem key={status} value={status} className="capitalize">
                        {status}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Impact (1-5): {formData.impact}</Label>
              <Slider
                data-testid="risk-impact-slider"
                value={[formData.impact]}
                onValueChange={([value]) => setFormData({ ...formData, impact: value })}
                min={1}
                max={5}
                step={1}
                className="mt-3"
              />
              <div className="flex justify-between text-xs text-slate-400 mt-1">
                <span>Minimal</span>
                <span>Critical</span>
              </div>
            </div>

            <div>
              <Label>Likelihood (1-5): {formData.likelihood}</Label>
              <Slider
                data-testid="risk-likelihood-slider"
                value={[formData.likelihood]}
                onValueChange={([value]) => setFormData({ ...formData, likelihood: value })}
                min={1}
                max={5}
                step={1}
                className="mt-3"
              />
              <div className="flex justify-between text-xs text-slate-400 mt-1">
                <span>Rare</span>
                <span>Almost Certain</span>
              </div>
            </div>

            <div className="bg-slate-50 border border-slate-200 rounded-sm p-3">
              <p className="text-sm text-slate-600">
                Risk Score: <span className="font-bold">{formData.impact * formData.likelihood}</span>
                <span className="ml-2">
                  <RiskBadge score={formData.impact * formData.likelihood} />
                </span>
              </p>
            </div>

            {/* AI Assessment Section */}
            <div className="border border-purple-200 bg-purple-50 rounded-sm p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Sparkles size={18} className="text-purple-600" />
                  <span className="font-medium text-purple-900">AI Risk Assessment</span>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleAIAssess}
                  disabled={aiAssessing || (!formData.title && !formData.description)}
                  className="rounded-sm border-purple-300 text-purple-700 hover:bg-purple-100"
                  data-testid="ai-assess-btn"
                >
                  {aiAssessing ? (
                    <>
                      <Loader2 size={14} className="mr-1 animate-spin" />
                      Analyzing...
                    </>
                  ) : (
                    <>
                      <Sparkles size={14} className="mr-1" />
                      Analyze Risk
                    </>
                  )}
                </Button>
              </div>
              
              {aiAssessment && (
                <div className="space-y-3 mt-3 pt-3 border-t border-purple-200">
                  <div className="text-sm text-purple-800">
                    <strong>AI Analysis:</strong> {aiAssessment.ai_analysis?.substring(0, 200)}...
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div className="bg-white rounded p-2">
                      <div className="text-lg font-bold text-purple-700">{aiAssessment.suggested_impact}</div>
                      <div className="text-xs text-purple-600">Impact</div>
                    </div>
                    <div className="bg-white rounded p-2">
                      <div className="text-lg font-bold text-purple-700">{aiAssessment.suggested_likelihood}</div>
                      <div className="text-xs text-purple-600">Likelihood</div>
                    </div>
                    <div className="bg-white rounded p-2">
                      <div className="text-lg font-bold text-purple-700">{aiAssessment.suggested_risk_score}</div>
                      <div className="text-xs text-purple-600">Score</div>
                    </div>
                  </div>
                  {aiAssessment.mitigation_recommendations?.length > 0 && (
                    <div className="text-sm">
                      <strong className="text-purple-800">Recommendations:</strong>
                      <ul className="list-disc list-inside text-purple-700 mt-1">
                        {aiAssessment.mitigation_recommendations.slice(0, 3).map((rec, idx) => (
                          <li key={idx}>{rec}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <Button
                    type="button"
                    size="sm"
                    onClick={applyAIAssessment}
                    className="w-full bg-purple-600 hover:bg-purple-700 rounded-sm"
                    data-testid="apply-ai-btn"
                  >
                    Apply AI Suggestions
                  </Button>
                  {aiAssessment.fallback_mode && (
                    <p className="text-xs text-purple-600 text-center">
                      Using rule-based fallback (AI budget exceeded)
                    </p>
                  )}
                </div>
              )}
            </div>

            <div>
              <Label>Risk Owner</Label>
              <Input
                data-testid="risk-owner-input"
                value={formData.owner}
                onChange={(e) => setFormData({ ...formData, owner: e.target.value })}
                placeholder="e.g., Security Team"
                className="mt-1.5 rounded-sm"
              />
            </div>

            <div>
              <Label>Treatment Plan</Label>
              <Textarea
                data-testid="risk-treatment-input"
                value={formData.treatment_plan}
                onChange={(e) => setFormData({ ...formData, treatment_plan: e.target.value })}
                placeholder="Describe how this risk will be mitigated..."
                rows={3}
                className="mt-1.5 rounded-sm"
              />
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsDialogOpen(false)}
                className="rounded-sm"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                data-testid="submit-risk-btn"
                disabled={submitting}
                className="bg-slate-900 hover:bg-slate-800 rounded-sm"
              >
                {submitting ? 'Saving...' : editingRisk ? 'Update Risk' : 'Log Risk'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
